/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-await-in-loop */
import UserPersonalInfoApi, { personalInfoCheck } from '@/api/userPersonalInfo';
import ButtonBar from '@/components/button-bar';
import './store';
import type { UserVo } from '@/vo/user';
import { Debounce } from '@/shared/decorators/debounce';
import { Form } from '@hb/coffee';
import {
  Component,
  Mixins,
  Prop,
} from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { MaritalStatusEnum, UpdateZyqSceneEnum, UpdateZyqSourceEnum } from '@/api/userPersonalInfo/types';
import { SceneTypeEnum } from '@/enums/scene-type';
import { VerifySceneEnum } from '@/shared/verify-credit-controller/types';
import { CONTACT_SPOUSE_RELATION_CODE, VerifyEntireTypeEnum } from '@/views_v2/verify-credit/types';
import type { VerifyStageContext } from '@/views_v2/verify-credit/types';
import { InfoComplementField, ProfessionType, specArrCode } from '@/components/new-info-complete/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { VNode } from 'vue';
import { leadTrailTrim, trimAll } from '@/shared/utils/trim';
import { isConfigSupplyPersonalScene } from '@/views_v2/verify-credit/const';
import { IS_PRERENDERING } from '@hb/coffee-prerender';
import type { SupplementInfoField } from '@/components/supplement-info-fields/types';
import ContactForm from '@/views_v2/verify-credit/components/contact-form';
import type { ContactFormRef } from '@/views_v2/verify-credit/components/contact-form';
import { SupportComponents } from '@/components/supplement-info-fields/components';
import VerifyBasicPersonal from '../../../mixins/verify-basic-personal';
import BasicPersonalHeader from '../../basic-personal-header';
import {
  ProfileFieldEnum, AdditionalSupplyConfig,
  CommonNeedFillStaticFields,
  StateType as ProfileStateType,
  ProfileFormData,
} from './types';
import {
  ExcludeDynamicSupplyField,
  SelfCommodityId,
  makeProfessionBeforeIndustry,
} from './form-fields';
import { submitBtnClickCustomUbt } from './ubt.config';

import style from './index.module.scss';

const PersonalProfileStore = namespace('personalProfile');

@Component({
  name: ' PersonalProfile',
})
export default class PersonalProfile extends Mixins(VerifyBasicPersonal) {
  @Prop(Boolean) readonly processIsLastVerify!: boolean;

  @Prop(String) readonly verifyScene!: VerifySceneEnum;

  @Prop([String]) readonly commodityIds!: string[];

  @Prop(String) readonly verifyEntireType!: VerifyEntireTypeEnum;

  @Prop(Object) readonly verifyStageContext!: VerifyStageContext;

  @Prop(Boolean) readonly isVerifyStageHeaderUINew!: boolean;

  @Prop(Object) readonly queryParams!: Record<string, unknown>;

  @Prop(Boolean) readonly ocrAfterLoan!: boolean;

  @PersonalProfileStore.State formData!: ProfileStateType['formData'];

  @PersonalProfileStore.State userEmergencyContacts!: ProfileStateType['userEmergencyContacts'];

  @PersonalProfileStore.Action initFormData!: () => Promise<void>;

  @PersonalProfileStore.Action initSelectOptions!: () => Promise<void>;

  @PersonalProfileStore.Action $updateState!: (data: Dictionary) => void;

  @PersonalProfileStore.State userBaseId!: ProfileStateType['userBaseId'];

  /** 底部按钮文案 */
  private get confirmText() {
    if (this.verifyScene === VerifySceneEnum.MARKET_PRE_SUPPLEMENT) {
      return this.processIsLastVerify ? '保存并下载' : '下一步';
    }

    return this.processIsLastVerify ? '提交' : '下一步';
  }

  /** 是否提现流程 */
  private get isLoanProcess() {
    return [
      VerifyEntireTypeEnum.LOAN,
      VerifyEntireTypeEnum.PRE_CREDIT,
    ].includes(this.verifyEntireType);
  }

  /** 其他资料补充配置(读取服务端接口) */
  private additionalSupplyConfig: AdditionalSupplyConfig = [];

  /** 是否需要更新联系人: 1. 已婚 > 要求有一配偶  2. 未婚 > 要求无已配偶 */
  private shouldUpdateContacts = false;

  /** 联系人中 关系 是配偶 个数 */
  private get spouseContactList() {
    return (this.userEmergencyContacts || []).filter(
      (item) => Number(item.contactRelation) === CONTACT_SPOUSE_RELATION_CODE,
    );
  }

  /** 无需通用的静态字段(学历、月薪、住址等) */
  private get noNeedCommonStaticFields() {
    return isConfigSupplyPersonalScene(this.verifyScene);
  }

  /** 补充资料字段-请求服务通用入参 */
  private get supplySceneType(): SceneTypeEnum {
    if ([
      VerifyEntireTypeEnum.PRE_CREDIT,
      VerifyEntireTypeEnum.LOAN,
    ].includes(this.verifyEntireType)) {
      return SceneTypeEnum.LOAN;
    }

    return SceneTypeEnum.PREAPPLY;
  }

  async created(): Promise<void> {
    this.$ubtModuleExpose(
      'finance_editPlatform',
      'platform',
      this.platformValue,
    );
    // 有渠道 id 时，请求接口返回需要补充的资料数据
    const configList = this.commodityIds && this.commodityIds.length
      ? (await UserPersonalInfoApi.supplyNeedConfig(
        this.commodityIds,
        this.supplySceneType,
      )) : [];

    if (configList.length) {
      const config = [] as AdditionalSupplyConfig;
      if (this.noNeedCommonStaticFields) {
        /** 无基本个人信息四个字段场景，过滤id即可 */
        configList
          .filter((name) => name !== 'id')
          .forEach((name) => {
            config.push(name as ProfileFieldEnum);
          });
      } else {
        configList
          .filter((name) => (!ExcludeDynamicSupplyField.includes(name)))
          .forEach((name) => {
            config.push(name as ProfileFieldEnum);
          });
      }

      /** 渠道自定义字段，确保表单项 “职业”在前，“所属行业”在后 */
      makeProfessionBeforeIndustry(config);

      this.additionalSupplyConfig = config;
    }
  }

  protected async setRequireSpouseInfo(
    commodityId: string,
    maritalStatus: MaritalStatusEnum,
  ): Promise<void> {
    try {
      const {
        emergencyCheck,
        unMarriedCheck,
        riskRequireContactsCheck,
        lockEmergency,
        lockUnMarried,
      } = await personalInfoCheck(
        commodityId, maritalStatus,
      );

      this.$emit('updateVerifyStageContext', {
        requireSpouseInfo: !!emergencyCheck,
        requireNoSpouseInfo: !!unMarriedCheck,
        lockSpouseInfo: !!lockEmergency,
        lockNoSpouseInfo: !!lockUnMarried,
        requireContactsInfo: !!riskRequireContactsCheck,
      });
    } catch (err) {
      ubtSendError(err as Error);
    }
  }

  private async initSupplyFormFields() {
    const updateFormFields = {} as Dictionary;

    if (this.formData && this.formData.maritalStatus) {
      await this.specialHandleForMarital(this.formData.maritalStatus, updateFormFields);
    }

    const { profession, industry } = this.formData || {};

    // 针对自营B 有职业 行业, 但是不匹配 表单初始化时，行业 职业表单数据清空
    if (profession && industry
      && this.supplySceneType === SceneTypeEnum.LOAN
      && this.commodityIds.includes(SelfCommodityId)
      && (!(
        (profession === ProfessionType.AGRICULTURE && industry === 101)
        || (profession === ProfessionType.TRANSPORT && specArrCode.indexOf(industry) >= 0)
      ))
    ) {
      this.formData.industry = undefined;
      this.formData.profession = undefined;
    }

    this.$updateState({
      formData: {
        ...this.formData,
        ...updateFormFields,
      },
    });
  }

  async mounted(): Promise<void> {
    if (!IS_PRERENDERING) {
      await this.initFormData();

      this.initSupplyFormFields();
      this.$emit('ubtExposeModule');
    }
  }

  /** 单个动态表单组件的生成方法 */
  private createOneDynamicComponent(fieldName: ProfileFieldEnum, listIdx?: number) {
    const propName = fieldName as keyof Partial<ProfileFormData>;
    const ComponentElement = SupportComponents.getComponent(propName);

    if (ComponentElement && this.formData) {
      return (
        <ComponentElement
          ref={propName}
          v-model={this.formData[propName]}
          onChange={(val: string) => this.onChangeFormData(propName, val)}
        />);
    }

    ubtSendError(new Error(`profile ${fieldName} component not found`));
    return null;
  }

  /** 为所有联系人生成各自的其他联系人上下文 */
  private get contactsGroupContextList() {
    if (!this.formData?.updateUserEmergencyContacts?.length) return [];

    const { updateUserEmergencyContacts } = this.formData;
    return updateUserEmergencyContacts.map(
      (__, currentIdx) => updateUserEmergencyContacts
        .filter((_, contactIdx) => contactIdx !== currentIdx),
    );
  }

  /** 婚姻状态关联"联系人信息"-动态组件表单渲染 */
  private renderMaritalRelationContacts(): VNode[] {
    if (!this.formData?.updateUserEmergencyContacts?.length) return [];

    const {
      requireSpouseInfo, requireNoSpouseInfo,
      lockSpouseInfo, lockNoSpouseInfo,
    } = (this.verifyStageContext || {});

    return this.formData?.updateUserEmergencyContacts.map((contactItem, idx) => {
      let showCheckSpouseTip = false;
      let lockSelectedSpouse = false;
      let disableSelectSpouse = false;

      /**
       * 在“已婚”锁定1个“配偶”联系人的情况下：
       * 联系人1：关系锁死配偶（前面formData已赋值）
       * 联系人2：关系不能选择配偶
       */
      if (requireSpouseInfo || lockSpouseInfo) {
        showCheckSpouseTip = idx === 0;

        if (contactItem.contactRelation === CONTACT_SPOUSE_RELATION_CODE) {
          lockSelectedSpouse = true;
        } else {
          disableSelectSpouse = true;
        }
      } else if (requireNoSpouseInfo || lockNoSpouseInfo) {
        /**
         * 在不是已婚(未婚、离异...)锁定无”配偶“联系人的情况下：
         * 联系人n：关系不能选择配偶(n >= 1)
         */
        disableSelectSpouse = true;
      }

      const onChange = (
        propName: keyof UserVo.ContactInfo,
        propValue: string | number,
      ) => {
        if (!this.formData.updateUserEmergencyContacts[idx]) {
          ubtSendError(new Error('updateUserEmergencyContacts is empty'));
        }

        this.formData.updateUserEmergencyContacts.splice(idx, 1, {
          ...this.formData.updateUserEmergencyContacts[idx],
          [propName]: propValue,
        });
      };

      return (
        <ContactForm
          class={style.contactItem}
          ref={`contactForm${idx + 1}`}
          contactItem={contactItem}
          commodityId={this.commodityIds[0]}
          contextOtherContacts={this.contactsGroupContextList[idx] || []}
          onChange={
            (name: keyof UserVo.ContactInfo, value: string | number) => onChange(name, value)
          }
          lockSelectedSpouse={lockSelectedSpouse}
          disableSelectSpouse={disableSelectSpouse}
        >
          <div class={style.contactItemTitle}>
            <div class={style.contactItemName}>联系人{idx + 1}</div>
              {showCheckSpouseTip && (
                <div class={style.contactItemTip}>
                  <p class={style.contactItemTipIcon} />
                  <p class={style.contactItemTipText}>请确认配偶信息</p>
                </div>
              )}
          </div>
        </ContactForm>
      );
    });
  }

  /** 检验个人资料表单是否通过 */
  private async validatePassedProfileForm(): Promise<boolean> {
    let validateResult = true;
    const fieldsValidate = async (fieldList: string[]) => {
      const len = fieldList.length;

      for (let i = 0; i < len; i++) {
        const name = fieldList[i];
        const $fieldRef = this.$refs[name] as InfoComplementField;

        if ($fieldRef && typeof $fieldRef.onValidator === 'function' && Boolean(await $fieldRef.onValidator()) === false) {
          validateResult = false;
        }
      }
    };

    try {
      await fieldsValidate(CommonNeedFillStaticFields);
      await fieldsValidate(this.additionalSupplyConfig);

      const waitUpdateContactFormRefCount = this.formData.updateUserEmergencyContacts?.length;

      /** 婚姻-锁死”联系人信息“form表单验证 */
      if (waitUpdateContactFormRefCount) {
        for (let i = 0; i < waitUpdateContactFormRefCount; i++) {
          const contactFormRef = this.$refs[`contactForm${i + 1}`] as ContactFormRef;

          if (contactFormRef && (await contactFormRef.checkFormValidator()) === false) {
            validateResult = false;
          }
        }
      }
      return validateResult;
    } catch (err) {
      return true;
    }
  }

  private async onChangeFormData(
    propName: keyof Partial<ProfileFormData>,
    propValue: string | number,
  ) {
    const updateFormFields = { [propName]: propValue } as Dictionary;
    // 自营 B渠道 根据行业限定职业
    if (propName === ProfileFieldEnum.PROFESSION && this.commodityIds.includes(SelfCommodityId)) {
      const $industryRefElement = this.$refs.industry as SupplementInfoField;

      if ($industryRefElement && typeof $industryRefElement.changeOptions === 'function') {
        $industryRefElement.changeOptions(propValue);
      }
    }
    /** 婚姻状态-特殊处理(联系人信息有无配偶的锁定) */
    if (propName === ProfileFieldEnum.MARITAL_STATUS) {
      await this.specialHandleForMarital(propValue, updateFormFields);
    }

    this.$updateState({
      formData: {
        ...this.formData,
        ...updateFormFields,
      },
    });
  }

  /** 个人指定的4条基本信息字段(学历、月薪、居住地、详细地址) */
  private renderSpecifyBaseFields(): VNode[] {
    /** 普通流程的supply字段场景 */
    if (this.noNeedCommonStaticFields) {
      return [];
    }

    return CommonNeedFillStaticFields.map(
      (name) => this.createOneDynamicComponent(name),
    ).filter((it) => !!it) as VNode[];
  }

  /** 个人动态配置的信息字段表单渲染 */
  private renderDynamicConfigFields(): VNode[] {
    const dynamicFields: (VNode)[] = [];

    /** 接口返回需要补充的资料字段，授信&提现补充资料场景 */
    if (this.additionalSupplyConfig.length) {
      this.additionalSupplyConfig.forEach(
        (name) => {
          const dynamicComponent = this.createOneDynamicComponent(name);

          if (dynamicComponent) {
            dynamicFields.push(dynamicComponent);
          }
        },
      );
    }

    return dynamicFields;
  }

  private render() {
    return (
      <div class={[style.container]} ref="profile">
        <BasicPersonalHeader
          isVerifyStageHeaderUINew={this.isVerifyStageHeaderUINew}
          verifyScene={this.verifyScene}
          isLoanProcess={this.verifyEntireType === VerifyEntireTypeEnum.LOAN}
          ocrAfterLoan={this.ocrAfterLoan}
        />
        <Form divider round class={style.formBox} ref="form">
          {...this.renderSpecifyBaseFields()}
          {...this.renderDynamicConfigFields()}
        </Form>
        {...this.renderMaritalRelationContacts()}
        <div class={style.buttonBox}>
          <ButtonBar
            style={this.submitBtnInTransparent ? { backgroundColor: 'transparent', boxShadow: 'none' } : null}
            confirmText={this.confirmText}
            onConfirm={this.onSubmitButtonClick}
          />
        </div>
      </div>
    );
  }

  /** 验证动态的联系人“手机号”的正确性 */
  private validateDynamicContactPhone(): void {
    try {
      const waitUpdateContactList = this.formData.updateUserEmergencyContacts;

      waitUpdateContactList.forEach((_, idx) => {
        const contactFormRef = this.$refs[`contactForm${idx + 1}`] as ContactFormRef;

        if (contactFormRef
            && contactFormRef.getContactFormData().contactPhone) {
          contactFormRef.checkAssignFieldValidator(ProfileFieldEnum.CONTACT_PHONE);
        }
      });
    } catch (err) {
      ubtSendError(err as Error);
    }
  }

  /** 用户-婚姻状态特殊处理（自营-关联到紧急联系人） */
  private async specialHandleForMarital(
    fieldValue: string | number,
    updateFormFields: {
      updateUserEmergencyContacts?: UserVo.ContactInfo[] } = { updateUserEmergencyContacts: [] },
  ) {
    if (this.isLoanProcess) {
      const maritalStatus = Number(fieldValue) as MaritalStatusEnum;
      /** 更新 verifyStageContext */
      await this.setRequireSpouseInfo(this.commodityIds[0], maritalStatus);
      const {
        requireSpouseInfo,
        requireNoSpouseInfo,
        requireContactsInfo,
        lockSpouseInfo,
        lockNoSpouseInfo,
      } = this.verifyStageContext;

      this.shouldUpdateContacts = false;

      /** 选择”已婚“，需要补充配偶联系人信息，（联系人无中配偶）,联系人1需要锁定配偶, 联系人2不能选择配偶 */
      if (maritalStatus === MaritalStatusEnum.MARRIED
        && (requireSpouseInfo || requireContactsInfo)) {
        /** 展示更新联系人1，2信息表单 */
        updateFormFields.updateUserEmergencyContacts = this.userEmergencyContacts.map(({
          contactName, contactPhone, contactRelation, contactNo,
        }) => ({
          contactNo,
          contactName,
          contactPhone,
          contactRelation,
        }));

        if (lockSpouseInfo) {
          const [updateContact1, updateContact2] = updateFormFields.updateUserEmergencyContacts;
          const { contactRelation: r1 } = updateContact1;
          const { contactRelation: r2 } = updateContact2;

          /** 如果联系人关系都不是配偶，则设置联系人1为配偶 */
          if (r1 !== CONTACT_SPOUSE_RELATION_CODE && r2 !== CONTACT_SPOUSE_RELATION_CODE) {
            updateContact1.contactRelation = CONTACT_SPOUSE_RELATION_CODE;
          }

          /** 如果联系人关系都为配偶，则清空联系人2关系 */
          if (r1 === CONTACT_SPOUSE_RELATION_CODE && r2 === CONTACT_SPOUSE_RELATION_CODE) {
            updateContact2.contactRelation = undefined;
          }
        }

        this.shouldUpdateContacts = true;
      } else if (
        maritalStatus !== MaritalStatusEnum.MARRIED
        && ((requireNoSpouseInfo && this.spouseContactList.length) || requireContactsInfo)) {
        /** 选择除”已婚“的其他：未婚等，联系人1，2 不能包含配偶，所以若原来已填写过配偶信息，则需要更新修正 */
        updateFormFields.updateUserEmergencyContacts = this.userEmergencyContacts.map(({
          contactName, contactPhone, contactRelation, contactNo,
        }) => ({
          contactNo,
          contactName,
          contactPhone,
          contactRelation,
        }));

        /** 在风控都展示联系人信息情况下，如果锁无配偶信息，在切换时如果联系人信息为配偶，清空联系人关系 */
        if (lockNoSpouseInfo) {
          const [updateContact1, updateContact2] = updateFormFields.updateUserEmergencyContacts;
          const { contactRelation: r1 } = updateContact1;
          const { contactRelation: r2 } = updateContact2;

          if (r1 === CONTACT_SPOUSE_RELATION_CODE) {
            updateContact1.contactRelation = undefined;
          }

          if (r2 === CONTACT_SPOUSE_RELATION_CODE) {
            updateContact2.contactRelation = undefined;
          }
        }

        this.shouldUpdateContacts = true;
      } else {
        /** 联系人表单重置，避免多个联系人表单信息叠加 */
        this.formData.updateUserEmergencyContacts.splice(0);
      }

      /**
       * 命中风控规则，展示联系人信息时，校验联系人手机号是否可用
       * 延迟100ms，保证联系人表单状态-已完成更新
       */
      setTimeout(() => {
        if (requireContactsInfo) {
          this.validateDynamicContactPhone();
        }
      }, 100);
    }
  }

  @Debounce(200)
  private onSubmitButtonClick() {
    this.onSubmit();
  }

  private async onSubmit(): Promise<void> {
    this.$emit('ubtClickSubmit');
    /** 各场景自定义-点击提交按钮的上报埋点 */
    submitBtnClickCustomUbt(this);

    if (await this.checkPreVerifySubmitState(this.verifyScene)) {
      const isValidationPassed = await this.validatePassedProfileForm();

      if (!isValidationPassed) return;

      if (this.formData && this.submitBtnInLoading === false) {
        this.submitBtnInLoading = true;

        try {
          const {
            educationDegree, liveAddress, liveAddressDetails, preTaxMonthSalary,
          } = this.formData;
          const baseInfo = {
            educationDegree,
            liveAddress,
            liveAddressDetails: leadTrailTrim(liveAddressDetails),
            preTaxMonthSalary,
          } as ProfileFormData;
          let { userBaseId } = this;

          if (!this.noNeedCommonStaticFields) {
            const data = await UserPersonalInfoApi.updateZYQ({
              ...baseInfo,
              updateSource: UpdateZyqSourceEnum.HOME_PAGE,
              updateScene: UpdateZyqSceneEnum.BASE_INFO,
            });

            userBaseId = data.userBaseId;
          }

          if (
            this.additionalSupplyConfig.length || this.formData.updateUserEmergencyContacts.length
          ) {
            const updateQuery = { commodityIds: this.commodityIds, id: userBaseId } as Dictionary;

            this.additionalSupplyConfig.forEach((fieldName) => {
              const name = fieldName as unknown as ProfileFieldEnum;

              updateQuery[name] = this.formData
                ? ((this.formData as unknown as Dictionary<string>)[name]) : null;
            });

            /** 提现场景：全量传输基本字段到 supplySave 接口（只传有值的字段） */
            if (this.supplySceneType === SceneTypeEnum.LOAN) {
              CommonNeedFillStaticFields.forEach((fieldName) => {
                const value = this.formData?.[fieldName as keyof ProfileFormData];
                // 只传有值的字段，字符串类型额外排除空串
                if (value !== undefined && value !== null && value !== '') {
                  updateQuery[fieldName] = value;
                }
              });
            }

            /** 是否需要更新多个联系人 */
            const isManyUpdateContacts = this.formData.updateUserEmergencyContacts.length >= 1;

            if (this.shouldUpdateContacts && isManyUpdateContacts) {
              updateQuery.emergencyContactList = this.formData.updateUserEmergencyContacts.map(({
                contactName, contactPhone, contactRelation,
              }) => ({
                contactName,
                contactPhone: trimAll(contactPhone),
                contactRelation,
              }));
            } else if (
              this.shouldUpdateContacts && !isManyUpdateContacts) {
              const {
                contactName, contactPhone, contactRelation,
              } = this.formData.updateUserEmergencyContacts[0] || {};

              updateQuery.emergencyContacts = {
                contactName,
                contactPhone: trimAll(contactPhone),
                contactRelation,
              };
            }

            /** 提交“supplyNeedConfig接口”标识 需要额外填写的字段 */
            await UserPersonalInfoApi.supplySave(
              updateQuery,
              this.supplySceneType,
            );
          }

          this.$emit('resolveProcessVerify');
        } catch (error) {
          ubtSendError(error as Error);
        } finally {
          this.submitBtnInLoading = false;
        }
      }
    }
  }
}
